import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, alpha, Box, CircularProgress, Container, IconButton, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessSpecificationCategory } from '../../../../MicrospecDynamicUI/Models/Business/BusinessSpecificationCategory';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { SPECIFICATION_CATEGORIES_COLLECTION } from '../../../constants/collectionPaths';
import UpsertSpecificationCategory from './UpsertSpecificationCategory';

const UpdateSpecificationCategory: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { translate } = useMicrospecThemeLanguageContext().localizationProvider;
  const { categoryId } = useParams<{ categoryId: string }>();
  const db = useMicrospecDynamicUIContext().cloudProvider.firestore?.instance;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<BusinessSpecificationCategory | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!db || !categoryId) { setError(translate('specCategory.missingParameters')); setLoading(false); return; }
      try {
        const data = await getDocById(null, null, db, SPECIFICATION_CATEGORIES_COLLECTION, categoryId, false);
        if (data) setItem(data as BusinessSpecificationCategory);
        else setError(translate('specCategory.notFound'));
      } catch (err: any) {
        setError(err.message || translate('specCategory.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [db, categoryId, translate]);

  const handleCancel = () => navigate('/manager/specification-categories/manage');

  if (loading) return <Container maxWidth="lg" sx={{ py: 4 }}><Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box></Container>;
  if (error) return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!item) return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="warning">{translate('specCategory.notFound')}</Alert></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleCancel} sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1), '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) } }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900] }}>
            {translate('specCategory.editTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">{translate('specCategory.updateDescription')}</Typography>
        </Box>
      </Box>
      <UpsertSpecificationCategory
        specCategoryId={categoryId}
        onSave={() => { window.location.href = '/manager/specification-categories/manage'; }}
        onCancel={handleCancel}
      />
    </Container>
  );
};

export default UpdateSpecificationCategory;
